import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import NewWcnTable, { DataTableColumn } from '@/components/datatable/NewWcnTable';
import HealthStatusCell from './HealthStatusCell';
import { HEALTH_ORDER, toHealthKey } from '@core/utils/statusUtils';
import { formatLastActivity } from '@core/utils/formatUtils';
import { sortWithAlphabetTiebreaker } from '@core/utils/sortUtils';
import { useTranslation } from 'react-i18next';
import { ConfirmationDialogWithIcon } from '@/components';
import { ConfirmationDialogRef } from '@/components/dialog/ConfirmationDialogWithIcon.tsx';
import { equalsIgnoreCase } from '@core/utils';
import { ItemStatus } from '@features/shopautomation/types.ts';
import { useAuthStore } from '@core/store';
import { Role } from '@core/types';

const COL_DEVICE = 250;
const COL_STATE = 180;
const COL_DETAILS = 200;
const COL_ACTIVITIES = 160;
const COL_ACTION = 160;
const COL_ACT_NOSUPER = 360;

interface DeviceSubTableProps {
    items: ItemStatus[];
    onReboot: (item: ItemStatus) => Promise<void>;
}

const DeviceSubTable: React.FC<DeviceSubTableProps> = ({ items, onReboot }) => {
    const { t } = useTranslation();
    const confirmDialogRef = useRef<ConfirmationDialogRef>(null);
    const [pendingItem, setPendingItem] = useState<ItemStatus | null>(null);
    const { hasRole } = useAuthStore();
    const isSuperAdmin = hasRole(Role.SuperAdmin);

    const mappedItems = useMemo(
        () =>
            items.map((item) => ({
                ...item,
                healthOrder: HEALTH_ORDER[toHealthKey(item.healthStatusType)]
            })),
        [items]
    );

    const getRebootCommand = useCallback((item: ItemStatus | null) => {
        const rebootCommand = item?.commands?.find((cmd) => equalsIgnoreCase(cmd, 'reboot'));
        if (!rebootCommand || !item?.deviceOrServiceType || !item.shopAutomationId) return '';
        return rebootCommand;
    }, []);

    const columns = useMemo((): DataTableColumn<ItemStatus & { healthOrder: number }>[] => {
        const cols: DataTableColumn<ItemStatus & { healthOrder: number }>[] = [
            {
                value: 'name',
                text: t('device'),
                sortable: true,
                width: COL_DEVICE,
                render: (row) => <Typography variant="body2">{row.name}</Typography>
            }
        ];

        cols.push(
            {
                value: 'healthOrder',
                text: t('state'),
                sortable: true,
                width: COL_STATE,
                render: (row) => (
                    <HealthStatusCell
                        healthStatus={toHealthKey(row.healthStatusType)}
                        name={row.name}
                        errorMessage={row.error}
                    />
                )
            },
            ...(isSuperAdmin
                ? [
                      {
                          value: 'ipAddress',
                          text: t('details'),
                          sortable: true,
                          width: COL_DETAILS,
                          render: (row: any) => {
                              let versionLabel = row.version;
                              if (row.version && row.version !== 'unknown') versionLabel = `V. ${row.version}`;
                              return (
                                  <div className="scc-details-cell">
                                      <Typography variant="body2" color="text.secondary">
                                          {row.ipAddress}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                          {versionLabel}
                                      </Typography>
                                  </div>
                              );
                          }
                      }
                  ]
                : []),
            {
                value: 'lastActivityDateTime',
                text: t('activities'),
                sortable: true,
                width: isSuperAdmin ? COL_ACTIVITIES : COL_ACT_NOSUPER,
                align: 'left',
                render: (row) => (
                    <Typography variant="body2" color="text.secondary">
                        {formatLastActivity(row.lastActivityDateTime)}
                    </Typography>
                )
            },
            {
                value: 'action',
                text: t('action'),
                sortable: false,
                width: COL_ACTION,
                isAction: true,
                align: 'center',
                render: (row) =>
                    !!getRebootCommand(row) && (
                        <Box>
                            <IconButton
                                size="small"
                                title={t('reboot')}
                                onClick={() => {
                                    setPendingItem(row);
                                    confirmDialogRef.current?.openDialog();
                                }}
                            >
                                <i className="bi bi-arrow-counterclockwise" />
                            </IconButton>
                        </Box>
                    )
            }
        );

        return cols;
    }, [t, isSuperAdmin, getRebootCommand]);

    const handleReboot = async (item: ItemStatus | null) => {
        if (!item) return;
        try {
            await onReboot(item);
        } catch {}
    };

    return (
        <Box className="scc-sub-table-wrapper">
            <NewWcnTable
                columns={columns}
                data={mappedItems}
                noDataMessage={t('noData')}
                sorting={{ defaultSortBy: 'healthOrder' }}
                customSort={sortWithAlphabetTiebreaker}
            />

            <ConfirmationDialogWithIcon
                dialogRef={confirmDialogRef}
                heading={`${t('commandDevice', { command: getRebootCommand(pendingItem) })}?`}
                message={t('commandConfirmMessage', {
                    command: getRebootCommand(pendingItem),
                    device: pendingItem?.name
                })}
                onConfirm={() => handleReboot(pendingItem)}
                confirmText="yesReboot"
                cancelText="noCancel"
                icon={<i className="bi bi-bootstrap-reboot" />}
            />
        </Box>
    );
};

export default DeviceSubTable;
